import { useEffect, useMemo, useState } from 'react';

export type SortByOption = 'az' | 'za' | 'popular' | 'newest';
export type TimeframeOption = 'live_now' | 'today' | 'tomorrow' | 'this_weekend';
export type PrizePoolOption = 'descending' | 'ascending';
export type GameTypeOption =
  | 'squares'
  | 'daily_draw'
  | 'bingo'
  | 'pickems'
  | 'survivor'
  | 'bracket';
export type SportOption =
  | 'nba'
  | 'nfl'
  | 'mlb'
  | 'soccer'
  | 'tennis'
  | 'golf'
  | 'ice_hockey'
  | 'table_tennis'
  | 'track_and_field'
  | 'mls'
  | 'ufc'
  | 'nascar'
  | 'the_masters'
  | 'premier_league';

export interface LobbyFilters {
  sortBy: SortByOption | null;
  timeframe: TimeframeOption | null;
  prizePools: PrizePoolOption | null;
  gameTypes: GameTypeOption[];
  sports: SportOption[];
}

export const DEFAULT_LOBBY_FILTERS: LobbyFilters = {
  sortBy: null,
  timeframe: null,
  prizePools: null,
  gameTypes: [],
  sports: [],
};

interface Props {
  isOpen: boolean;
  value: LobbyFilters;
  availableGameTypes: GameTypeOption[];
  availableSports: SportOption[];
  onClose: () => void;
  onApply: (next: LobbyFilters) => void;
}

const SORT_BY_OPTIONS: Array<{ id: SortByOption; label: string }> = [
  { id: 'az', label: 'A-Z' },
  { id: 'za', label: 'Z-A' },
  { id: 'popular', label: 'Popular' },
  { id: 'newest', label: 'Newest' },
];

const TIMEFRAME_OPTIONS: Array<{ id: TimeframeOption; label: string }> = [
  { id: 'live_now', label: 'Live Now' },
  { id: 'today', label: 'Today' },
  { id: 'tomorrow', label: 'Tomorrow' },
  { id: 'this_weekend', label: 'This Weekend' },
];

const PRIZE_POOL_OPTIONS: Array<{ id: PrizePoolOption; label: string }> = [
  { id: 'descending', label: 'Descending' },
  { id: 'ascending', label: 'Ascending' },
];

const GAME_TYPE_OPTIONS: Array<{ id: GameTypeOption; label: string }> = [
  { id: 'squares', label: 'Squares' },
  { id: 'daily_draw', label: 'Daily Draw' },
  { id: 'bingo', label: 'Game Grid' },
  { id: 'pickems', label: "Pickems'" },
  { id: 'survivor', label: 'Survivor' },
  { id: 'bracket', label: 'Bracket' },
];

const SPORT_OPTIONS: Array<{ id: SportOption; label: string }> = [
  { id: 'nba', label: 'NBA' },
  { id: 'nfl', label: 'NFL' },
  { id: 'mlb', label: 'MLB' },
  { id: 'soccer', label: 'Soccer' },
  { id: 'tennis', label: 'Tennis' },
  { id: 'golf', label: 'Golf' },
  { id: 'ice_hockey', label: 'Ice Hockey' },
  { id: 'table_tennis', label: 'Table Tennis' },
  { id: 'track_and_field', label: 'Track and Field' },
  { id: 'mls', label: 'MLS' },
  { id: 'ufc', label: 'UFC' },
  { id: 'nascar', label: 'NASCAR' },
  { id: 'the_masters', label: 'The Masters' },
  { id: 'premier_league', label: 'Premier League' },
];

const DEFAULT_VISIBLE_SPORT_COUNT = 6;

function toggleMultiSelect<T extends string>(values: T[], next: T): T[] {
  if (values.includes(next)) return values.filter(v => v !== next);
  return [...values, next];
}

function PrizeIcon({ type }: { type: PrizePoolOption }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="5" y1="7" x2="11" y2="7" />
      <line x1="5" y1="12" x2="9" y2="12" />
      <line x1="5" y1="17" x2="7" y2="17" />
      {type === 'descending' ? (
        <>
          <line x1="16" y1="5" x2="16" y2="18" />
          <polyline points="12,14 16,18 20,14" />
        </>
      ) : (
        <>
          <line x1="16" y1="18" x2="16" y2="5" />
          <polyline points="12,9 16,5 20,9" />
        </>
      )}
    </svg>
  );
}

function pruneFilters(
  filters: LobbyFilters,
  availableGameTypes: GameTypeOption[],
  availableSports: SportOption[]
): LobbyFilters {
  return {
    ...filters,
    gameTypes: filters.gameTypes.filter(id => availableGameTypes.includes(id)),
    sports: filters.sports.filter(id => availableSports.includes(id)),
  };
}

export default function LobbyFilterView({
  isOpen,
  value,
  availableGameTypes,
  availableSports,
  onClose,
  onApply,
}: Props) {
  const [draft, setDraft] = useState<LobbyFilters>(value);
  const [showAllSports, setShowAllSports] = useState(false);

  const gameTypeOptions = useMemo(
    () => GAME_TYPE_OPTIONS.filter(option => availableGameTypes.includes(option.id)),
    [availableGameTypes]
  );
  const sportOptions = useMemo(
    () => SPORT_OPTIONS.filter(option => availableSports.includes(option.id)),
    [availableSports]
  );

  useEffect(() => {
    if (!isOpen) return;
    setDraft(pruneFilters(value, availableGameTypes, availableSports));
    setShowAllSports(false);
  }, [isOpen, value, availableGameTypes, availableSports]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const hasSelections = useMemo(
    () => draft.gameTypes.length > 0 || draft.sports.length > 0,
    [draft.gameTypes.length, draft.sports.length]
  );
  const showSportViewAll = sportOptions.length > DEFAULT_VISIBLE_SPORT_COUNT;
  const visibleSportOptions = showAllSports
    ? sportOptions
    : sportOptions.slice(0, DEFAULT_VISIBLE_SPORT_COUNT);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-[1px]"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Lobby filters"
    >
      <div
        className="absolute inset-x-3 bottom-0 mx-auto max-w-3xl rounded-t-[30px] border border-white/10 bg-[#07090d] px-4 pb-4 pt-2 shadow-2xl md:inset-x-6 md:px-6"
        onClick={event => event.stopPropagation()}
      >
        <div className="mb-2 flex justify-center">
          <span className="h-1 w-14 rounded-full bg-white/10" />
        </div>

        <div className="max-h-[80vh] overflow-y-auto pb-20 no-scrollbar">
          <header className="mb-4 flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-extrabold tracking-tight text-white">Filters</h2>
              <p className="mt-0.5 text-xs font-semibold text-gray-300">All Games</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="mt-0.5 rounded-full p-1.5 text-white/90 transition hover:bg-white/10 hover:text-white"
              aria-label="Close filters"
            >
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
              >
                <line x1="5" y1="5" x2="19" y2="19" />
                <line x1="19" y1="5" x2="5" y2="19" />
              </svg>
            </button>
          </header>

          <section className="mb-4">
            <h3 className="mb-2 text-xl font-bold text-white">Sort By</h3>
            <div className="no-scrollbar -mx-1 flex gap-2 overflow-x-auto px-1">
              {SORT_BY_OPTIONS.map(option => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() =>
                    setDraft(prev => ({
                      ...prev,
                      sortBy: prev.sortBy === option.id ? null : option.id,
                    }))
                  }
                  className={[
                    'shrink-0 rounded-full border px-4 py-1.5 text-sm font-semibold leading-none transition',
                    draft.sortBy === option.id
                      ? 'border-white/70 bg-white/12 text-white'
                      : 'border-white/20 bg-black/30 text-gray-200 hover:border-white/45',
                  ].join(' ')}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </section>

          <section className="mb-4">
            <h3 className="mb-2 text-xl font-bold text-white">Timeframe</h3>
            <div className="no-scrollbar -mx-1 flex gap-2 overflow-x-auto px-1">
              {TIMEFRAME_OPTIONS.map(option => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() =>
                    setDraft(prev => ({
                      ...prev,
                      timeframe: prev.timeframe === option.id ? null : option.id,
                    }))
                  }
                  className={[
                    'shrink-0 rounded-full border px-4 py-1.5 text-sm font-semibold leading-none transition',
                    draft.timeframe === option.id
                      ? 'border-white/70 bg-white/12 text-white'
                      : 'border-white/20 bg-black/30 text-gray-200 hover:border-white/45',
                  ].join(' ')}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </section>

          <section className="mb-4">
            <h3 className="mb-2 text-xl font-bold text-white">Prize Pools</h3>
            <div className="no-scrollbar -mx-1 flex gap-2 overflow-x-auto px-1">
              {PRIZE_POOL_OPTIONS.map(option => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() =>
                    setDraft(prev => ({
                      ...prev,
                      prizePools: prev.prizePools === option.id ? null : option.id,
                    }))
                  }
                  className={[
                    'inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-semibold leading-none transition',
                    draft.prizePools === option.id
                      ? 'border-white/70 bg-white/12 text-white'
                      : 'border-white/20 bg-black/30 text-gray-200 hover:border-white/45',
                  ].join(' ')}
                >
                  <PrizeIcon type={option.id} />
                  {option.label}
                </button>
              ))}
            </div>
          </section>

          {gameTypeOptions.length > 0 ? (
            <section className="mb-4">
              <h3 className="mb-2 text-xl font-bold text-white">Game Types</h3>
              <div className="grid grid-cols-3 gap-2">
                {gameTypeOptions.map(option => {
                  const selected = draft.gameTypes.includes(option.id);
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() =>
                        setDraft(prev => ({
                          ...prev,
                          gameTypes: toggleMultiSelect(prev.gameTypes, option.id),
                        }))
                      }
                      className={[
                        'flex items-center gap-2 rounded-xl border px-2.5 py-2 text-left text-sm font-medium leading-tight transition',
                        selected
                          ? 'border-[#53D337]/70 bg-[#53D337]/15 text-white'
                          : 'border-white/10 bg-white/10 text-gray-100 hover:border-white/35',
                      ].join(' ')}
                    >
                      <span
                        className={[
                          'h-3.5 w-3.5 shrink-0 rounded-[4px] border',
                          selected ? 'border-[#53D337] bg-[#53D337]/20' : 'border-white/30',
                        ].join(' ')}
                      />
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </section>
          ) : null}

          {sportOptions.length > 0 ? (
            <section className="mb-3">
              <h3 className="mb-2 text-xl font-bold text-white">Sport / League</h3>
              <div className="flex flex-wrap gap-2">
                {visibleSportOptions.map(option => {
                  const selected = draft.sports.includes(option.id);
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() =>
                        setDraft(prev => ({
                          ...prev,
                          sports: toggleMultiSelect(prev.sports, option.id),
                        }))
                      }
                      className={[
                        'flex items-center gap-2 rounded-xl border px-3 py-2 text-left text-sm font-medium leading-tight transition',
                        selected
                          ? 'border-[#53D337]/70 bg-[#53D337]/15 text-white'
                          : 'border-white/10 bg-white/10 text-gray-100 hover:border-white/35',
                      ].join(' ')}
                    >
                      <span
                        className={[
                          'h-3.5 w-3.5 shrink-0 rounded-[4px] border',
                          selected ? 'border-[#53D337] bg-[#53D337]/20' : 'border-white/30',
                        ].join(' ')}
                      />
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </section>
          ) : null}

          {showSportViewAll ? (
            <button
              type="button"
              onClick={() => setShowAllSports(prev => !prev)}
              className={[
                'mb-5 w-full rounded-xl border px-4 py-2 text-sm font-semibold transition',
                hasSelections
                  ? 'border-white/30 bg-white/12 text-white hover:bg-white/16'
                  : 'border-white/15 bg-white/8 text-gray-200 hover:border-white/30',
              ].join(' ')}
            >
              {showAllSports ? 'View Less' : 'View All'}
            </button>
          ) : null}
        </div>

        <div className="absolute inset-x-4 bottom-4 grid grid-cols-2 gap-3 bg-[#07090d] pt-2 md:inset-x-6">
          <button
            type="button"
            onClick={() => setDraft(DEFAULT_LOBBY_FILTERS)}
            className="rounded-xl border border-white/10 bg-white/10 px-3 py-2.5 text-base font-semibold text-white transition hover:bg-white/16"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={() => onApply(draft)}
            className="rounded-xl border border-[#53D337] bg-[#53D337] px-3 py-2.5 text-base font-semibold text-black transition hover:bg-[#49be30]"
          >
            Apply Changes
          </button>
        </div>
      </div>
    </div>
  );
}
